import { combineReducers } from 'redux';
import { getFormValues, change as changeForm, startSubmit, stopSubmit, blur } from 'redux-form';
import saveAs from 'file-saver';
import _ from 'lodash';
import axios from 'axios';
import humps from 'humps';
import qs from 'qs';
import i18next from 'i18next';

import * as Urls from 'src/constants/EndpointUrls';
import { serializeHttpGetParams, concatParamsToUrl, redirectTo } from 'src/utils/Http';
import { paramsWithFile } from 'src/utils/Form';
import createNamedWrapperReducer from 'src/reducers/createNamedWrapperReducer';
import { listReducer, itemReducer } from 'src/reducers/common';
import { newItemFailure, editItemFailure, getItemErrors, setItemError, setItemExtra, getItemExtra } from 'src/reducers/common/itemReducer';
import {
  getListQueries,
  fetchList,
  fetchListSuccess,
  fetchListFailure,
  setExtras,
  getExtras,
  setQueries,
  addQuery
} from 'src/reducers/common/listReducer';
import { REDUCER_NAME as MEMO_REDUCER_NAME } from 'src/reducers/memos/memos';
import { setOptions, getSelectOptions } from 'src/reducers/selectOptions';
import { setGlobalErrors, setGlobalSuccesses, setGlobalWarnings } from 'src/reducers/global';

import { EMPLOYEE_PAY_CSV_FORM, EMPLOYEE_SEARCH_FORM, EMPLOYEE_PAY_FORM, PAYROLL_FB_DATA_FORM } from 'src/constants/FormNames';
import {
  takePaysSearchQueries,
  takePayConfirmedStateFromSearchForm,
  takePaymentRangeFormSearchForm,
  FORM_NAME_PAYS_SEARCH,
  fetchUniquePaymentDates
} from 'src/reducers/searchForm';
import { scrollToTop } from 'src/utils/Utils';

export const REDUCER_NAME = 'employeePays';
export const setEmployeePaysQueries = queries => setQueries(REDUCER_NAME, queries);
export const getEmployeePayQueries = state => getListQueries(REDUCER_NAME, state);
export const getEmployeePayListExtras = state => getExtras(REDUCER_NAME, state);
export const clearErrors = () => dispatch => dispatch(newItemFailure(REDUCER_NAME, []));
export const setErrors = errors => dispatch => dispatch(newItemFailure(REDUCER_NAME, errors));
export const getEmployeePayItemErrors = state => getItemErrors(REDUCER_NAME, state);
export const getEmployeePayCsvFormValues = state => getFormValues(EMPLOYEE_PAY_CSV_FORM)(state);
export const clearFBDataService = () => dispatch => {
  const clearFields = [
    'kbankServiceSelect',
    'krungsriServiceSelect',
    'smbcServiceSelect',
    'bangkokServiceSelect',
    'transferType',
    'directCreditSelect',
    'payeeChargeCodeSelect',
    'scbServiceSelect',
    'scbCompanyId',
    'scbAnywhere',
    'companyCode',
    'customerBatchReference',
    'icashType',
    'smartServiceCompanyId',
    'smartServiceBatchRef',
    'cutOffTime',
    'isSameDay'
  ];
  clearFields.forEach(clearField => {
    dispatch(changeForm(PAYROLL_FB_DATA_FORM, clearField, ''));
  });
};

export const getDownloadPayrollFBDataErrors = state => getItemExtra(REDUCER_NAME, state).payrollFBErrors;
export const getDownloadInhabitantTaxFBDataErrors = state => getItemExtra(REDUCER_NAME, state).inhabitantTaxFBErrors;
export const getConfirmPayrollGroupErrors = state => getItemExtra(REDUCER_NAME, state).changePayrollGroupErrors;
export const getExportPndErrors = state => getItemExtra(REDUCER_NAME, state).pndErrors;
export const getExportSsoErrors = state => getItemExtra(REDUCER_NAME, state).ssoErrors;

export const setDownloadPayrollFBDataErrors = payrollFBErrors => setItemExtra(REDUCER_NAME, { payrollFBErrors });
export const setDownloadPndDataErrors = pndErrors => setItemExtra(REDUCER_NAME, { pndErrors });
export const setDownloadSsoDataErrors = ssoErrors => setItemExtra(REDUCER_NAME, { ssoErrors });
export const setDownloadInhabitantTaxFBDataErrors = inhabitantTaxFBErrors => setItemExtra(REDUCER_NAME, { inhabitantTaxFBErrors });
export const clearDownloadPayrollFBDataErrors = () => setItemExtra(REDUCER_NAME, { payrollFBErrors: '' });
export const clearDownloadPndDataErrors = () => setItemExtra(REDUCER_NAME, { pndErrors: '' });
export const clearDownloadSsoDataErrors = () => setItemExtra(REDUCER_NAME, { ssoErrors: '' });
export const clearDownloadInhabitantTaxFBDataErrors = () => setItemExtra(REDUCER_NAME, { inhabitantTaxFBErrors: '' });
export const setConfirmPayrollGroupErrors = changePayrollGroupErrors => setItemExtra(REDUCER_NAME, { changePayrollGroupErrors });

// Async Action Creators
export const fetchEmployeePays = queries => async dispatch => {
  try {
    dispatch(blur(FORM_NAME_PAYS_SEARCH, 'uniquePaymentDate', _.get(queries, 'uniquePaymentDate'), true));
    dispatch(fetchList(REDUCER_NAME));
    const response = await axios.get(`${Urls.SEARCH_EMPLOYEE_PAY_URL}`, { params: queries });
    const { totalCount = 0, displayFrom = 0, displayTo = 0 } = response.data.payload;
    const payload = {
      data: response.data.payload.employeePays,
      pageCount: response.data.payload.totalPages,
      totalCount,
      displayFrom,
      displayTo
    };
    dispatch(fetchListSuccess(REDUCER_NAME, payload));
    dispatch(setExtras(REDUCER_NAME, response.data.payload.targetPeriod));
    dispatch(setExtras(MEMO_REDUCER_NAME, response.data.payload.memos));
  } catch (exception) {
    dispatch(fetchListFailure(REDUCER_NAME, exception.response.data.errors.messages));
    dispatch(setExtras(REDUCER_NAME, {}));
  }
};

export const fetchPayWithEmployeeCondition = data => async (dispatch, getState) => {
  try {
    // Get parameter from form data
    const { nameCnt, groupIds, employmentTypeIds, occupationIds, payrollRulesGroup, positionIds, noMemo, otherSearch } = data;
    let { memoColorIds } = data;
    if (memoColorIds) {
      memoColorIds = Object.keys(memoColorIds)
        .map(key => (memoColorIds[key] ? key : null))
        .toString();
    }
    // Current query
    const originQueries = getListQueries(REDUCER_NAME, getState());
    // 1ページ目から検索
    const newQueries = {
      ...originQueries,
      nameCnt,
      groupIds,
      employmentTypeIds,
      occupationIds,
      payrollRulesGroup,
      positionIds,
      noMemo,
      memoColorIds,
      otherSearch,
      page: 1
    };
    dispatch(setQueries(REDUCER_NAME, newQueries));
    // Re fetch employee bonus
    dispatch(fetchEmployeePays());
  } catch (exception) {
    dispatch(fetchListFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const editEmployeePay = (data, employeePayId) => async dispatch => {
  try {
    const cloneData = { ...data };
    const motivations = cloneData.motivationStreaks?.map(motivation => ({
      id: motivation.id,
      label: motivation.label,
      value: motivation.value
    }));

    delete cloneData.motivationStreaks;

    const httpBody = {
      ...cloneData,
      customizeAllowances: cloneData.customizeAllowances,
      payments: [...cloneData.payments, ...cloneData.payments[0].description, ...cloneData.payments[1].description],
      deductions: [...cloneData.deductions, ...cloneData.deductions.filter(field => field.description).flatMap(field => field.description)],
      motivations
    };

    // for income tax support and social security contribution support
    const childFields = cloneData.payments[0].description.filter(field => field.description);
    if (!_.isUndefined(childFields)) {
      childFields.forEach(field => field.description.map(element => httpBody.payments.push(element)));
    }

    dispatch(startSubmit(EMPLOYEE_PAY_FORM));
    const updatePayUrl = concatParamsToUrl(Urls.UPDATE_EMPLOYEE_PAY_URL, { id: employeePayId });
    await axios.put(updatePayUrl, httpBody);
    // const editPayUrl = concatParamsToUrl(Urls.EMPLOYEE_PAY_EDIT, { id: employeePayId });
    // // TODO 編集画面に遷移させているが本来は詳細画面に遷移させる
    // redirectTo(editPayUrl);

    // Reload to update to the newest data
    // and keep tracking list employee pays to select next employee
    window.location.reload();
  } catch (exception) {
    scrollToTop();
    dispatch(stopSubmit(EMPLOYEE_PAY_FORM));
    dispatch(setItemError(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const downloadPayrollFBData = params => async dispatch => {
  try {
    dispatch(clearDownloadPayrollFBDataErrors());

    const query = qs.stringify(humps.decamelizeKeys({ ...params }), { arrayFormat: 'brackets' });
    // ここは一見すると2回同じ処理を叩いてるように見えるが、redirectTo(中身はlocation.href)だとerrorをキャッチできないため、
    // 先に1回xhrでリクエストしている
    const url = `${Urls.DOWNLOAD_PAYROLL_FB_DATA}?${query}`;
    await axios.get(url);
    redirectTo(url);
  } catch (e) {
    const errs = _.get(e, ['response', 'data', 'errors', 'messages']) || [e.message];
    dispatch(setDownloadPayrollFBDataErrors(errs));
  }
};

export const downloadPndData = params => async dispatch => {
  try {
    dispatch(clearDownloadPndDataErrors());

    const query = qs.stringify(humps.decamelizeKeys({ ...params }), { arrayFormat: 'brackets' });
    const url = `${Urls.DOWNLOAD_PND_DATA}?${query}`;
    await axios.get(url);
    redirectTo(url);
  } catch (e) {
    const errs = _.get(e, ['response', 'data', 'errors', 'messages']) || [e.message];
    dispatch(setDownloadPndDataErrors(errs));
  }
};

export const downloadSsoData = params => async dispatch => {
  try {
    dispatch(clearDownloadSsoDataErrors());

    const query = qs.stringify(humps.decamelizeKeys({ ...params }), { arrayFormat: 'brackets' });
    const url = `${Urls.DOWNLOAD_SSO_DATA}?${query}`;
    await axios.get(url);
    redirectTo(url);
  } catch (e) {
    const errs = _.get(e, ['response', 'data', 'errors', 'messages']) || [e.message];
    dispatch(setDownloadSsoDataErrors(errs));
  }
};

export const downloadInhabitantTaxFBData = (params, payrollGroupSelectorType) => async dispatch => {
  try {
    dispatch(clearDownloadInhabitantTaxFBDataErrors());
    let data = { ...params };
    const { payForRetirement, ...rest } = params;
    if (!+payForRetirement) data = rest;

    if (payrollGroupSelectorType === 'each_payroll_rules_group') {
      data.yearMonth = data.uniquePaymentDate;
      data.payrollRulesGroups = data.uniquePayrollRuleGroups;
    }

    const query = qs.stringify(humps.decamelizeKeys(data), { arrayFormat: 'brackets' });
    const url = `${Urls.DOWNLOAD_INHABITANT_TAX_FB_DATA}?${query}`;
    const response = await fetch(url);
    if (!response.ok) {
      const json = await response.json();
      throw new Error(json.errors.messages);
    }
    const responseBlob = await response.blob();
    const blob = new Blob([responseBlob], { type: 'text/csv' });
    const fileName = decodeURI(response.headers.get('content-disposition').match(/filename="(.*)"/)[1]);
    saveAs(blob, fileName);
  } catch (e) {
    const errs = _.get(e, ['response', 'data', 'errors', 'messages']) || [e.message];
    dispatch(setDownloadInhabitantTaxFBDataErrors(errs));
  }
};

// default clientPayrollGroup from select option
// when search get client payrollgroup from extras
export const getClientPayrollRulesGroups = state => {
  let clientPayrollRulesGroups = getSelectOptions(state, 'clientPayrollRulesGroups');
  const extras = getEmployeePayListExtras(state);
  if (!!extras && !!extras.clientPayrollRulesGroups) {
    clientPayrollRulesGroups = extras.clientPayrollRulesGroups;
  }
  return clientPayrollRulesGroups;
};

export const changeLock = (employeePayId, currentConfirmed) => async dispatch => {
  try {
    const changeLockUrl = currentConfirmed ? Urls.EMPLOYEE_PAY_UNLOCK : Urls.EMPLOYEE_PAY_LOCK;
    await axios.put(changeLockUrl, { id: employeePayId });

    redirectTo(concatParamsToUrl(Urls.EMPLOYEE_PAY_EDIT, { id: employeePayId }));
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

const fetchJobInfo = (state, params) => {
  const uniquePayrollRuleGroups = getSelectOptions(state, 'uniquePayrollRuleGroups');
  const uniquePaymentDates = getSelectOptions(state, 'uniquePaymentDates');
  const { payrollRulesGroups, yearMonth } = params;

  const payrollRuleGroup = uniquePayrollRuleGroups.find(i => i.value === payrollRulesGroups);
  const paymentDate = uniquePaymentDates.find(i => i.value === yearMonth);

  return `${payrollRuleGroup.label} ${paymentDate.label}`;
};

export const fetchConfirmationPayStatus = (visibleOnly = false) => async dispatch => {
  try {
    const resp = await axios.get(`${Urls.EMPLOYEE_CONFIRM_STATUS}?visible_only=${visibleOnly}`);
    dispatch(setExtras(REDUCER_NAME, { ...resp.data.payload }));
  } catch (e) {
    const msgs = _.get(e, ['response', 'data', 'errors', 'messages']);
    if (!_.isEmpty(msgs)) {
      dispatch(setGlobalErrors(msgs));
    }
  }
};

export const changePayrollGroupConfirm = data => async (dispatch, getState) => {
  try {
    const currentState = getState();
    const { releaseDate = '', releaseHour = '' } = data;
    const queries = takePaymentRangeFormSearchForm(currentState, FORM_NAME_PAYS_SEARCH);
    const params = { ...queries, ...data, releaseDate: releaseDate.concat(' ').concat(releaseHour) };
    const isConfirm = takePayConfirmedStateFromSearchForm(currentState, FORM_NAME_PAYS_SEARCH);
    const changeConfirmUrl = isConfirm ? Urls.EMPLOYEE_PAY_UNCONFIRM : Urls.EMPLOYEE_PAY_CONFIRM;

    await axios.put(changeConfirmUrl, { ...params, jobInfo: fetchJobInfo(currentState, params) });
    if (isConfirm) {
      const { payrollRulesGroups } = params;
      dispatch(setGlobalSuccesses(i18next.t('reducers.employeePays.salary_confirmation_has_been_canceled')));
      dispatch(fetchUniquePaymentDates(FORM_NAME_PAYS_SEARCH, payrollRulesGroups, false, false));
    } else {
      dispatch(setGlobalWarnings(i18next.t('reducers.employeePays.salary_confirmation_in_process')));
    }
    dispatch(fetchConfirmationPayStatus(true));
  } catch (e) {
    const msgs = _.get(e, ['response', 'data', 'errors', 'messages']);
    if (!_.isEmpty(msgs)) {
      dispatch(setGlobalErrors(msgs));
    }
  }
};

export const setInvisibleConfimPayJob = confirmPayJobIds => async dispatch => {
  try {
    const resp = await axios.put(Urls.EMPLOYEE_CONFIRM_SET_CONFIRM_JOB_INVISIBLE, { jobIds: confirmPayJobIds });
    dispatch(setExtras(REDUCER_NAME, { ...resp.data.payload }));
    dispatch(fetchConfirmationPayStatus(true));
  } catch (e) {
    const msgs = _.get(e, ['response', 'data', 'errors', 'messages']);
    if (!_.isEmpty(msgs)) {
      dispatch(setGlobalErrors(msgs));
    }
  }
};

export const getConfirmJobs = state => getExtras(REDUCER_NAME, state).confirmJobs;

export const setConfirmJobsExtras = confirmJobs => dispatch => {
  dispatch(setExtras(REDUCER_NAME, { confirmJobs }));
};

export const rollbackEmployeePay = employeePayId => async dispatch => {
  try {
    const rollbackEmployeePayUrl = Urls.EMPLOYEE_PAY_ROLLBACK;
    await axios.put(rollbackEmployeePayUrl, { id: employeePayId });

    redirectTo(concatParamsToUrl(Urls.EMPLOYEE_PAY_EDIT, { id: employeePayId }));
  } catch (exception) {
    dispatch(setItemError(REDUCER_NAME, { rollbackEmployeePay: exception.response.data.errors.messages }));
  }
};

export const allRollbackEmployeePay = (payrollRulesGroups, yearMonth) => async dispatch => {
  try {
    const allRollbackEmployeePayUrl = Urls.EMPLOYEE_PAY_ALL_ROLLBACK;
    await axios.put(allRollbackEmployeePayUrl, { payrollRulesGroups, yearMonth });

    window.location.reload();
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, { rollbackEmployeePay: exception.response.data.errors.messages }));
  }
};

export const resetPayrollRulesGroup = newPayrollRulesGroups => (dispatch, getState) => {
  const { payrollRulesGroup } = takePaymentRangeFormSearchForm(getState(), FORM_NAME_PAYS_SEARCH);
  if (payrollRulesGroup && !newPayrollRulesGroups.split('_').includes(`${payrollRulesGroup}`)) {
    dispatch(addQuery(REDUCER_NAME, 'payrollRulesGroup', undefined));
    dispatch(changeForm(EMPLOYEE_SEARCH_FORM, 'payrollRulesGroup', ''));
  }
};

// 給与振込一覧表
export const getShowPayFbDataUrl = state => {
  const { yearMonth, payrollRulesGroups } = takePaymentRangeFormSearchForm(state, FORM_NAME_PAYS_SEARCH);
  const params = serializeHttpGetParams({ yearMonth, payrollRulesGroups });
  const payFbDataUrl = `${Urls.SHOW_PAY_FB_DATA_PAGE_URL}?${params}`;
  return payFbDataUrl;
};

export const fetchPayFbData = queries => async dispatch => {
  try {
    dispatch(fetchList(REDUCER_NAME));
    dispatch(setExtras(REDUCER_NAME, { totalTransferAmount: 0.0 }));

    const params = serializeHttpGetParams({
      payroll_rules_groups: queries.payroll_rules_groups,
      year_month: queries.year_month,
      sort: _.get(queries, 'sort', 'staffCode'),
      order: _.get(queries, 'order', 'asc')
    });
    const fetchUrl = `${Urls.SEARCH_PAY_FB_DATA}?${params}`;
    const response = await axios.get(fetchUrl);

    const payload = { data: response.data.payload.employeePays, pageCount: 0 }; // ページは使わない
    const targetPeriod = response.data.payload.targetPeriod;
    const totalTransferAmount = response.data.payload.totalTransferAmount;

    dispatch(fetchListSuccess(REDUCER_NAME, payload));
    dispatch(setExtras(REDUCER_NAME, { ...targetPeriod, totalTransferAmount }));
  } catch (exception) {
    dispatch(setGlobalErrors(exception.response.data.errors.messages));
  }
};

export const fetchInhabitantTaxData = (formValues, isFetchGrouped) => async dispatch => {
  try {
    dispatch(fetchList(REDUCER_NAME));
    let params = null;
    if (isFetchGrouped) {
      params = {
        grouped_payment_months: formValues.groupedPaymentMonths
      };
    } else {
      params = takePaysSearchQueries(formValues);
    }

    const fetchUrl = `${Urls.SEARCH_INHABITANT_TAX_DATA}?${serializeHttpGetParams(params)}`;
    const response = await axios.get(fetchUrl);

    const employeePays = response.data.payload.employeePays;
    const payload = { data: employeePays, pageCount: 0 }; // ページは使わない

    const { totalAmounts, totalEmployees, targetPeriod, totalResigned, cityIds } = response.data.payload;
    dispatch(fetchListSuccess(REDUCER_NAME, payload));
    dispatch(setExtras(REDUCER_NAME, { totalAmounts, totalEmployees, targetPeriod, totalResigned, cityIds }));
  } catch (exception) {
    dispatch(fetchListFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const getDownloadPayCsvJobs = state => getExtras(REDUCER_NAME, state).downloadPayCsvJobs;

export const fetchDownloadPayCsvJobs = visibleOnly => async dispatch => {
  try {
    const resp = await axios.get(`${Urls.FETCH_DOWNLOAD_PAY_CSV_JOB_STATUS}?visible_only=${visibleOnly}`);
    dispatch(setExtras(REDUCER_NAME, { ...resp.data.payload }));
  } catch (e) {
    const msgs = _.get(e, ['response', 'data', 'errors', 'messages']);
    if (!_.isEmpty(msgs)) {
      dispatch(setGlobalErrors(msgs));
    }
  }
};

export const getCompletedDownloadPayCsvJob = state => getExtras(REDUCER_NAME, state).completedDownloadPayCsvJob;

export const setCompletedDownloadPayCsvJob = job => async dispatch => {
  dispatch(setExtras(REDUCER_NAME, { completedDownloadPayCsvJob: job }));
};

export const fetchCompletedDownloadPayCsvJob = queries => async dispatch => {
  try {
    const resp = await axios.get(Urls.FETCH_COMPLETED_DOWNLOAD_PAY_CSV_JOB_STATUS, {
      params: humps.decamelizeKeys(queries)
    });
    dispatch(setCompletedDownloadPayCsvJob(resp.data.payload.downloadPayCsvJob));
  } catch (e) {
    const msgs = _.get(e, ['response', 'data', 'errors', 'messages']);
    if (!_.isEmpty(msgs)) {
      dispatch(setGlobalErrors(msgs));
    }
  }
};

export const setDownloadPayCsvJobInvisible = taskIds => async dispatch => {
  try {
    const resp = await axios.put(Urls.SET_DOWNLOAD_PAY_CSV_JOB_INVISIBLE, { jobIds: taskIds });
    dispatch(setExtras(REDUCER_NAME, { ...resp.data.payload }));
    dispatch(fetchDownloadPayCsvJobs(true));
  } catch (e) {
    const msgs = _.get(e, ['response', 'data', 'errors', 'messages']);
    if (!_.isEmpty(msgs)) {
      dispatch(setGlobalErrors(msgs));
    }
  }
};

// send request to server action
// Download template Csv
export const downloadTemplateCsv = () => async (dispatch, getState) => {
  try {
    const formValues = getFormValues(EMPLOYEE_PAY_CSV_FORM)(getState()) || {};
    const queries = getEmployeePayQueries(getState());
    const params = { ...queries, ...formValues };
    dispatch(clearErrors());
    const resp = await axios.post(Urls.DOWNLOAD_EMPLOYEE_PAY_CSV, params);
    if (!_.isEmpty(resp.data) && !!_.get(resp, ['data', 'payload', 'downloadUrl'])) {
      redirectTo(resp.data.payload.downloadUrl);
    } else {
      dispatch(setGlobalWarnings(i18next.t('reducers.employeePays.file_is_being_processed_please_wait_for_a_while')));
    }
  } catch (exception) {
    const errors = _.get(exception, ['response', 'data', 'errors', 'messages']) || exception.message;
    dispatch(setGlobalErrors(errors));
  }
};

export const getImportPayCsvJobs = state => getExtras(REDUCER_NAME, state).importPayCsvJobs;

export const fetchImportPayCsvJobs = visibleOnly => async dispatch => {
  try {
    const resp = await axios.get(`${Urls.FETCH_IMPORT_CSV_JOB_STATUS}?visible_only=${visibleOnly}`);
    dispatch(setExtras(REDUCER_NAME, { ...resp.data.payload }));
  } catch (e) {
    const msgs = _.get(e, ['response', 'data', 'errors', 'messages']);
    if (!_.isEmpty(msgs)) {
      dispatch(setGlobalErrors(msgs));
    }
  }
};

export const setImportPayCsvJobInvisible = taskIds => async dispatch => {
  try {
    const resp = await axios.put(Urls.SET_IMPORT_CSV_JOB_INVISIBLE, { jobIds: taskIds });
    dispatch(setExtras(REDUCER_NAME, { ...resp.data.payload }));
    dispatch(fetchImportPayCsvJobs(true));
  } catch (e) {
    const msgs = _.get(e, ['response', 'data', 'errors', 'messages']);
    if (!_.isEmpty(msgs)) {
      dispatch(setGlobalErrors(msgs));
    }
  }
};

// Upload modified Csv
export const importEmployeePayCsv = data => async (dispatch, getState) => {
  try {
    dispatch(startSubmit(EMPLOYEE_PAY_CSV_FORM));
    const { yearMonth, payrollRulesGroups } = getEmployeePayQueries(getState());
    const params = paramsWithFile(data, ['employee_pay_csv']);
    params.append('year_month', yearMonth);
    params.append('payroll_rules_groups', payrollRulesGroups);
    params.append('job_info', data.jobInfo);
    dispatch(clearErrors());
    dispatch(setGlobalWarnings(i18next.t('reducers.employeePays.salary_data_upload_in_process')));
    await axios.post(Urls.IMPORT_EMPLOYEE_PAY_CSV, params);
  } catch (exception) {
    scrollToTop();
    dispatch(setErrors(exception.response.data.errors.messages));
  } finally {
    dispatch(stopSubmit(EMPLOYEE_PAY_CSV_FORM));
  }
};

// 公開日を変更
export const updatePayReleaseDate = (data, callbackOnSuccess) => async (dispatch, getState) => {
  try {
    const currentState = getState();
    const queries = takePaymentRangeFormSearchForm(currentState, FORM_NAME_PAYS_SEARCH);
    const { payrollRulesGroups = '' } = queries;
    const { releaseDate = '', releaseHour = '' } = data;

    if (_.isEmpty(releaseDate) || _.isEmpty(releaseHour)) {
      dispatch(setGlobalErrors(i18next.t('reducers.employeePays.please_check_disclosure_date_or_check_email')));
      return;
    }
    const params = { ...queries, ...data, releaseDate: `${releaseDate} ${releaseHour}` };
    await axios.put(Urls.CHANGE_RELEASE_DATE_EMPLOYEE_PAYS, params);

    dispatch(setGlobalSuccesses(i18next.t('reducers.employeePays.disclosure_date_has_been_changed')));
    dispatch(fetchUniquePaymentDates(FORM_NAME_PAYS_SEARCH, payrollRulesGroups, false, false));
    if (typeof callbackOnSuccess === 'function') callbackOnSuccess();
  } catch (e) {
    const msgs = _.get(e, ['response', 'data', 'errors', 'messages']) || e.message;
    dispatch(setGlobalErrors(msgs));
  }
};

// PDF一括印刷
// 一括作成で作成されたものを印刷する
export const fetchPayReportsPdf = downloadId => async dispatch => {
  try {
    const url = concatParamsToUrl(Urls.BULK_PRINT_EMPLOYEE_PAY_REPORTS, { downloadId });
    const w = window.open(url, '', 'toolbar=0,menubar=0,location=0');
    if (_.isFunction(w.addEventListener)) {
      w.addEventListener('load', () => {
        w.print();
      });
    }
  } catch (err) {
    dispatch(setGlobalErrors(i18next.t('reducers.employeePays.an_error_occurred')));
  }
};

// PDF一括ダウンロード
// 一括作成で作成されたものをダウンロードする
export const fetchPayReportsZip = downloadId => async dispatch => {
  try {
    const url = concatParamsToUrl(Urls.BULK_DOWNLOAD_EMPLOYEE_PAY_REPORTS, { downloadId });
    redirectTo(url);
  } catch (err) {
    dispatch(setGlobalErrors(i18next.t('reducers.employeePays.an_error_occurred')));
  }
};

// PDF一括作成
export const setPayReportDownloadJob = (payrollRulesGroups, yearMonth, uniquePaymentDates, language, formatType) => async dispatch => {
  try {
    const params = {
      payrollRuleGroups: payrollRulesGroups,
      paymentDate: yearMonth,
      language,
      formatType
    };
    const response = await axios.post(Urls.CREATE_EMPLOYEE_PAY_REPORTS, params);
    if (response.status === 200) {
      const newPaymentDates = _.map(uniquePaymentDates, item => {
        if (item.value !== yearMonth) return item;
        if (language === 'vi') return { ...item, downloadObjVi: { ...response.data.payload.downloadObj.vi } };
        return { ...item, downloadObjEn: response.data.payload.downloadObj.en };
      });
      dispatch(setOptions({ uniquePaymentDates: newPaymentDates }));
      dispatch(setExtras(REDUCER_NAME, response.data.payload.downloadObj));
    }
  } catch (err) {
    dispatch(setGlobalErrors(i18next.t('reducers.employeePays.an_error_occurred')));
  }
};

export const fetchZipJobStatus = objId => async dispatch => {
  try {
    if (objId != null) {
      const response = await axios.get(`/clients/employee_pay_reports/${objId}/fetch_status`);
      dispatch(setExtras(REDUCER_NAME, response.data.payload.downloadObj));
    }
  } catch (err) {
    dispatch(setGlobalErrors(i18next.t('reducers.employeePays.an_error_occurred')));
  }
};

export const fetchCreatedPayReports = (payrollRuleGroupsId, paymentDate) => async dispatch => {
  try {
    if ((payrollRuleGroupsId != null, paymentDate != null)) {
      const response = await axios.get(
        `/clients/employee_pay_reports/${payrollRuleGroupsId}/fetch_created_pay_reports?payment_date=${paymentDate}`
      );
      dispatch(setExtras(REDUCER_NAME, response.data.payload));
    }
  } catch (err) {
    dispatch(setGlobalErrors(i18next.t('reducers.employeePays.an_error_occurred')));
  }
};

export const updatePaymentDate = data => async dispatch => {
  try {
    const { targetPaymentDate, uniquePaymentDates, selectedPaymentDate } = data;
    const paymentDateId = uniquePaymentDates.find(item => item.value === selectedPaymentDate)?.id;
    if (!paymentDateId) {
      dispatch(setGlobalErrors(i18next.t('reducers.employeePays.payment_date_not_found')));
      throw new Error(i18next.t('reducers.employeePays.payment_date_not_found'));
    }

    await axios.put(Urls.UPDATE_PAYMENT_DATE, { id: paymentDateId, targetPaymentDate });
    redirectTo(Urls.EMPLOYEE_PAY_LIST);
  } catch (error) {
    dispatch(setGlobalErrors(error.response.data.errors.messages[0]));
  }
};

export const fetchDashboardSummaryJobStatus = (visibleOnly = false) => async dispatch => {
  try {
    const response = await axios.get(`${Urls.DASHBOARD_SUMMARY_JOB_STATUS}?visible_only=${visibleOnly}`);
    dispatch(setExtras(REDUCER_NAME, { ...response.data.payload }));
  } catch (e) {
    const messages = _.get(e, ['response', 'data', 'errors', 'messages']);
    if (!_.isEmpty(messages)) {
      dispatch(setGlobalErrors(messages));
    }
  }
};

export const getDashboardSummaryJobs = state => getExtras(REDUCER_NAME, state).dashboardSummaryJobs;

export const setInvisibleDashboardSummaryJob = dashboardSummaryJobIds => async dispatch => {
  try {
    const response = await axios.put(Urls.SET_DASHBOAR_SUMMARY_JOB_INVISIBLE, { jobIds: dashboardSummaryJobIds });
    dispatch(setExtras(REDUCER_NAME, { ...response.data.payload }));
    dispatch(fetchDashboardSummaryJobStatus(true));
  } catch (e) {
    const messages = _.get(e, ['response', 'data', 'errors', 'messages']);
    if (!_.isEmpty(messages)) {
      dispatch(setGlobalErrors(messages));
    }
  }
};

export default combineReducers({
  list: createNamedWrapperReducer(listReducer, REDUCER_NAME),
  item: createNamedWrapperReducer(itemReducer, REDUCER_NAME)
});
